import { useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Shield, Mic, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function TolokyConsentDialog({ open, onOpenChange, onComplete }) {
  const [understood, setUnderstood] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleEnable = async () => {
    if (!understood) return;
    setSaving(true);
    try {
      await axios.post(`${API}/comms/toloky/consent`, { consented: true }, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Voice simulation enabled");
      onComplete?.(true);
      onOpenChange(false);
    } catch {
      toast.error("Failed to save consent");
    }
    setSaving(false);
  };

  const handleSkip = () => {
    onComplete?.(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-md rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-800">
            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
              <Mic className="w-4 h-4 text-violet-600" />
            </div>
            Toloky Voice Simulation Consent
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-700">
              Toloky can simulate your voice when translating to others for a natural experience.
            </p>
          </div>

          <div className="space-y-3 text-sm text-slate-600">
            <p className="font-medium text-slate-800">This means:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
                <span>Your voice will be analyzed in real-time (3-5 seconds)</span>
              </li>
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
                <span>The AI will mimic your tone, pitch, and speaking style</span>
              </li>
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <span>No voice samples or biometric data are ever stored</span>
              </li>
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
                <span>Only you can enable this for your own voice</span>
              </li>
            </ul>
          </div>

          <div className="flex items-start gap-2 pt-2">
            <Checkbox
              id="consent-check"
              checked={understood}
              onCheckedChange={setUnderstood}
              className="mt-0.5"
            />
            <label htmlFor="consent-check" className="text-sm text-slate-700 cursor-pointer">
              I understand and consent to voice simulation
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleEnable}
              disabled={!understood || saving}
              className="flex-1 bg-violet-600 hover:bg-violet-700 rounded-none"
            >
              {saving ? "Saving..." : "Enable Voice Simulation"}
            </Button>
            <Button
              onClick={handleSkip}
              variant="outline"
              className="flex-1 rounded-none"
            >
              Use Default Voice Only
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
